<script setup lang="ts">
import { computed } from 'vue'

import { Upload } from '@element-plus/icons-vue'

import SVGIcon from '@/components/UI/SVGIcon.vue'

import { checkFileMagicNumbers } from '@/utils/file/checkImageType'

import type { UploadFile } from 'element-plus'

interface Props {
  /** 已裁切的圖片 URL */
  croppedImage?: string | null
  /** 是否啟用自動抓取模式 */
  autoFetchEnabled?: boolean
  /** 是否禁用上傳 */
  disabled?: boolean
  /** 提示文字 */
  tooltipText?: string
  /** 顯示載入狀態 */
  loading?: boolean
  /** 上傳模式：simple（簡單圖示） 或 detailed（詳細提示） */
  uploadStyle?: 'simple' | 'detailed'
}

interface Emits {
  (e: 'change', file: UploadFile): void
  (e: 'error', message: string): void
}

const {
  croppedImage = null,
  autoFetchEnabled = false,
  disabled = false,
  tooltipText = '圖片最佳比例 1200x628px (jpg或png)，不得大於1MB',
  loading = false,
  uploadStyle = 'detailed'
} = defineProps<Props>()

const acceptFileTypes = '.jpg, .jpeg, .png'

const emit = defineEmits<Emits>()

// 上傳顯示狀態
const Display_State = {
  DEFAULT_THUMBNAIL: 'default', // 預設縮圖（僅自動抓取模式）
  UPLOADED_IMAGE: 'uploaded', // 已上傳圖片
  UPLOAD_PROMPT: 'prompt' // 上傳提示
} as const

type DisplayState = (typeof Display_State)[keyof typeof Display_State]

const displayState = computed<DisplayState>(() => {
  // 自動抓取模式且無圖片：顯示預設縮圖
  if (autoFetchEnabled && !croppedImage) {
    return Display_State.DEFAULT_THUMBNAIL
  }

  // 有圖片：顯示已上傳圖片
  if (croppedImage) {
    return Display_State.UPLOADED_IMAGE
  }

  // 無圖片：顯示上傳提示
  return Display_State.UPLOAD_PROMPT
})

const bgStyle = computed(() => (croppedImage ? { backgroundImage: `url(${croppedImage})` } : {}))

// 判斷是否在圖片上顯示上傳圖示
const showUploadIconOnImage = computed(() => {
  return !autoFetchEnabled && croppedImage
})

/**
 * 驗證並處理檔案上傳
 */
const handleFileChange = async (file: UploadFile): Promise<boolean | void> => {
  if (!file.raw) {
    return undefined
  }

  // 檔案大小驗證（1MB）
  const maxFileSize = 1048576
  if (file.size && file.size > maxFileSize) {
    const errorMsg = '檔案大小不能超過1MB'
    emit('error', errorMsg)
    ElMessage.error(errorMsg)
    return false
  }

  // 檔案類型驗證
  const isValidType = await checkFileMagicNumbers(file.raw)
  if (!isValidType) {
    const errorMsg = '只能上傳 JPG 或 PNG 格式的圖片！'
    emit('error', errorMsg)
    ElMessage.error(errorMsg)
    return false
  }
  emit('change', file)
  return undefined
}
</script>

<template>
  <el-upload
    class="image-uploader"
    :disabled="disabled"
    :drag="true"
    :auto-upload="false"
    :show-file-list="false"
    :accept="acceptFileTypes"
    :on-change="handleFileChange"
  >
    <div v-loading="loading" class="uploader-content" :style="bgStyle">
      <!-- 狀態 1: 預設縮圖 -->
      <div v-if="displayState === Display_State.DEFAULT_THUMBNAIL" class="state-default">
        <SVGIcon name="default-og-img-icon" :width="48" :height="48" />
        <span class="state-text">預設縮圖</span>
      </div>

      <!-- 狀態 2: 上傳提示 -->
      <div v-else-if="displayState === Display_State.UPLOAD_PROMPT" class="state-prompt">
        <!-- 詳細樣式 -->
        <template v-if="uploadStyle === 'detailed'">
          <el-tooltip :content="tooltipText" :show-arrow="false">
            <div class="prompt-detailed">
              <SVGIcon name="upload-img-icon" :width="40" :height="40" class="prompt-icon" />
              <span class="prompt-text">將圖片拖放到此處或</span>
              <span class="prompt-link">點擊上傳</span>
            </div>
          </el-tooltip>
        </template>

        <!-- 簡單樣式 -->
        <template v-else>
          <el-tooltip :content="tooltipText" :show-arrow="false">
            <el-icon class="el-icon--upload">
              <Upload />
            </el-icon>
          </el-tooltip>
        </template>
      </div>

      <!-- 狀態 3: 已上傳圖片 -->
      <!-- 當 autoFetchEnabled 為 false 且有圖片時，顯示上傳圖示覆蓋層 -->
      <div v-if="displayState === Display_State.UPLOADED_IMAGE && showUploadIconOnImage" class="upload-overlay">
        <el-tooltip :content="tooltipText" :show-arrow="false">
          <el-icon class="overlay-icon">
            <Upload />
          </el-icon>
        </el-tooltip>
      </div>
    </div>
  </el-upload>
</template>

<style lang="scss" scoped>
.image-uploader {
  position: relative;
  width: 100%;
}

.uploader-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 239px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

:deep(.el-upload-dragger) {
  height: 239px;
  padding: 0;
}

// 預設縮圖狀態
.state-default {
  @apply flex h-full w-full flex-col items-center justify-center gap-2 bg-app-black-50;
}

.state-text {
  @apply text-sm font-normal text-app-black-600;
}

// 上傳提示狀態
.state-prompt {
  @apply flex items-center justify-center;
}

.prompt-detailed {
  @apply flex flex-col items-center text-sm;
}

.prompt-icon {
  @apply mb-2;
}

.prompt-text {
  @apply mb-1 text-app-black-600;
}

.prompt-link {
  @apply text-app-brand-blue-500;
}

// Element Plus icon
:deep(.el-icon--upload) {
  width: 100%;
  height: 100%;
  font-size: 40px;
}

// 上傳圖示覆蓋層（顯示在圖片上方）
.upload-overlay {
  @apply absolute inset-0 flex items-center justify-center;
  background-color: rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.4);
  }
}

.overlay-icon {
  font-size: 48px;
  color: white;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
}
</style>
